import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import {
    acceptHMRUpdate,
    defineStore } from 'pinia';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import {
    convertProductLinesArrayToMap,
    hasPurchasedProductLine,
    setActiveProductLinesMap,
} from '@/data/flags/product-lines';
import {
    fetchProductLines,
    setFlagForType,
    setProductLineMetadata,
} from '@/stores/api/product-lines.api';
import {
    getFeedbackUnifiedLoginEnabled,
    isMetadataEnabledForAnyProductLine,
    useAdoptParityCustomRolesEnabled,
} from '@/stores/helpers/product-lines.helper';
import { buildProductLineFilterMetadata } from '@/stores/helpers/product-lines-filter.helper';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import type { ProductLineSchema } from '@/stores/types/product-line-schema.types';

export interface ProductLine {
    type: string;
    purchased: boolean;
    configured?: boolean;
    metadata?: {
        integrations?: {
            jira?: {
                enabled?: boolean;
            };
        };
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

interface ProductLinesState {
    map: Record<string, ProductLine>;
    error: unknown;
    fetching: boolean;
    schema: Array<ProductLineSchema>;
}

type ProductLineRequirement = string | [string, string];

let loadAllPromise: Promise<void> | null = null;

export function __resetProductLinesLoadAllPromiseForUnitTests (): void {
    loadAllPromise = null;
}

export function getInitialProductLinesState (): ProductLinesState {
    setActiveProductLinesMap({});

    return {
        map: {},
        error: null,
        fetching: false,
        schema: [],
    };
}

export const useProductLinesStore = defineStore('productLines', {
    state: getInitialProductLinesState,
    getters: {
        list: (state): ProductLine[] => values(state.map),
        productLineByType: (state) => (type: string): ProductLine | undefined => state.map[type],
        hasProductLinePurchased: (state) => (type: string, metadataName: string | null = null): boolean =>
            hasPurchasedProductLine(state.map, type, metadataName),
        hasCommandCenter (): boolean {
            return this.hasProductLinePurchased(PRODUCT_LINES.COMMAND_CENTER);
        },
        isVocListenFreemiumActive: (state): boolean => {
            if (!get(state.map, ['voc', 'purchased'])) return false;

            return !get(state.map.voc, 'metadata.freemium.listenPremium', true);
        },
        areProductLinesPurchased (): (productLines: ProductLineRequirement[]) => boolean {
            return (productLines) => {
                if (!productLines?.length) return true;

                return productLines.every((productLine) => {
                    if (Array.isArray(productLine)) {
                        return this.hasProductLinePurchased(...productLine);
                    }

                    return this.hasProductLinePurchased(productLine);
                });
            };
        },
        classicFeedbackEnabled (): boolean {
            const feedbackProductLine = this.productLineByType('feedback');
            if (!feedbackProductLine?.purchased) return false;

            const isUnifiedLoginEnabled = get(feedbackProductLine, 'metadata.unifiedLoginEnabled', false);

            return !!(feedbackProductLine.configured || isUnifiedLoginEnabled);
        },
        feedbackUnifiedLoginEnabled (): boolean {
            return getFeedbackUnifiedLoginEnabled(this.productLineByType('feedback'));
        },
        activeHasLTI (): boolean {
            const adoptEndUser = this.productLineByType('adoptEndUser');
            const adoptReseller = this.productLineByType('adoptReseller');

            return Boolean(
                get(adoptEndUser, 'metadata.ltiSSOEnabled', false) ||
                get(adoptReseller, 'metadata.ltiSSOEnabled', false),
            );
        },
        activeHasOIDC (): boolean {
            return Boolean(get(this.productLineByType('adoptReseller'), 'metadata.oidcEnabled', false));
        },
        activeHasAeuEventsOnly (): boolean {
            return Boolean(get(this.productLineByType('adoptReseller'), 'metadata.aeuEventsOnly', false));
        },
        useAdoptParityCustomRolesEnabled (): boolean {
            return useAdoptParityCustomRolesEnabled(
                this.productLineByType('adoptEndUser'),
                this.productLineByType('adopt'),
            );
        },
        sessionReplayRetentionDays (): number {
            return Number(get(this.productLineByType('sessionReplay'), 'metadata.recordingRetentionDays', 31));
        },
        hasCrossAppEnabled (): boolean {
            return Boolean(get(this.productLineByType('guides'), 'metadata.crossAppGuidesInOneUIEnabled', false));
        },
        maxPublicResourceCentersAllowed (): number {
            return Number(get(this.productLineByType('guides'), 'metadata.maxPublicResourceCentersAllowed', 0));
        },
        mobileAppsForAdoptPartnersEnabled: (): boolean => hasSegmentFlag('adoptMobile'),
        isFetching: (state): boolean => state.fetching,
        isMetadataEnabledForAnyProductLine: (state) => (metadataName: string): boolean =>
            isMetadataEnabledForAnyProductLine(state.map, metadataName),
        filterMetadata: (state) => buildProductLineFilterMetadata(state.schema),
        isEmailIntegrationEnabled (): boolean {
            const hasHubspotEmailIntegration = this.hasProductLinePurchased(
                'orchestrate',
                'hubspotEmailIntegrationEnabled',
            );
            const hasMarketoEmailIntegration = this.hasProductLinePurchased(
                'orchestrate',
                'marketoEmailIntegrationEnabled',
            );
            const hasHubspotSegmentFlag = hasSegmentFlag('hubspotEmail');
            const hasMarketoSegmentFlag = hasSegmentFlag('marketoEmail');

            return (
                (hasHubspotSegmentFlag && hasHubspotEmailIntegration) ||
                (hasMarketoSegmentFlag && hasMarketoEmailIntegration)
            );
        },
    },
    actions: {
        setMap ({ map }: { map: Record<string, ProductLine> }) {
            this.map = map;
            setActiveProductLinesMap(map);
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setSchema ({ schema }: { schema: Array<ProductLineSchema> }) {
            this.schema = schema;
        },
        setPropertyForType ({ type, property, value }: { type: string; property: string; value: unknown }) {
            this.map[type][property] = value;
            setActiveProductLinesMap(this.map);
        },
        updateProductLineMap ({ productLine }: { productLine: ProductLine }) {
            this.map[productLine.type] = productLine;
            setActiveProductLinesMap(this.map);
        },
        async hydrate ({ productLines }: { productLines?: ProductLine[] } = {}) {
            if (!productLines) {
                await this.loadAll({ reFetch: true });

                return;
            }

            this.setMap({ map: convertProductLinesArrayToMap(productLines) as Record<string, ProductLine> });
            if (this.activeHasOIDC) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useSubscriptionStore } = require('@/stores/subscription.store');
                await useSubscriptionStore().fetchOIDC();
            }
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise) return loadAllPromise;
            if (!isEmpty(this.map) && !reFetch) return;

            const promise = (async () => {
                this.fetching = true;
                try {
                    const productLines = await fetchProductLines();
                    this.setMap({ map: convertProductLinesArrayToMap(productLines) as Record<string, ProductLine> });
                } catch (error) {
                    this.error = error;
                    throw error;
                } finally {
                    this.fetching = false;
                }
            })();
            loadAllPromise = promise;
            try {
                await promise;
            } finally {
                if (loadAllPromise === promise) loadAllPromise = null;
            }
        },
        async setFlagForType ({ type, flag, set }: { type?: string; flag?: string; set?: boolean } = {}) {
            this.fetching = true;
            try {
                await setFlagForType({ type, flag, set });
                await this.loadAll({ reFetch: true });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async setProductLineMetadata (
            { id: subId, type, data }: { id?: string; type?: string; data?: unknown } = {},
        ) {
            this.fetching = true;
            try {
                const productLine = await setProductLineMetadata({ type, data, subId }) as ProductLine;
                this.updateProductLineMap({ productLine });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        updateMap ({ productLine }: { productLine: ProductLine }) {
            this.fetching = true;
            this.updateProductLineMap({ productLine });
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useSubscriptionStore } = require('@/stores/subscription.store');
            useSubscriptionStore().updateProductLines({ productLines: this.list });
            this.fetching = false;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useProductLinesStore, module.hot));
}
